export function fileUploader(): void {
  // creating variables for the elements
  const fileUploaderEl: HTMLDivElement | null = document.querySelector('.cmp-file-uploader');
  const droparea: HTMLDivElement | null = document.querySelector('.cmp-file-uploader__container');
  const button: HTMLButtonElement | null = document.querySelector('.cmp-file-uploader__button');
  const fileName: HTMLParagraphElement | null = document.querySelector(
    '.cmp-file-uploader__file-name'
  );
  const input: HTMLInputElement | null = document.querySelector('.cmp-file-uploader__input');
  const errorGeneric: HTMLParagraphElement | null = document.querySelector(
    '.cmp-file-uploader__error--generic'
  );
  const errorSize: HTMLParagraphElement | null = document.querySelector(
    '.cmp-file-uploader__error--size'
  );

  document.addEventListener('keypress', function (e: KeyboardEvent) {
    const actualTarget: HTMLElement = e.target as HTMLElement;
    if (
      actualTarget &&
      (actualTarget.tagName === 'BUTTON' ||
        actualTarget.tagName === 'INPUT' ||
        actualTarget.tagName === 'SELECT') &&
      e.code == 'Enter'
    ) {
      e.preventDefault();
    }
  });

  const errorType: HTMLParagraphElement | null = document.querySelector(
    '.cmp-file-uploader__error--type'
  );
  const errorMulti: HTMLParagraphElement | null = document.querySelector(
    '.cmp-file-uploader__error--multi'
  );
  const allowedExtensions = fileUploaderEl?.dataset['format']
    ?.split(',')
    .map((item) => item.trim().replace('.', ''));

  const maxFileSize = Number(fileUploaderEl?.dataset['sizeLimit']); // its in MB

  let uploadingStatus = false;

  const activeDragover = () => droparea?.classList.add('is-dragover');

  const inactiveDragover = () => droparea?.classList.remove('is-dragover');

  const activeUploading = (file: File) => {
    uploadingStatus = true;
    droparea?.classList.add('is-uploading');
    if (fileName) {
      if (file.name.length > 100) {
        fileName.innerHTML = file.name.substring(0, 100);
        return;
      }
      fileName.innerHTML = file.name;
    }
  };

  const inactiveUploading = () => {
    uploadingStatus = false;
    droparea?.classList.remove('is-uploading');
  };

  const removeErrorVisibility = () => {
    const errorElements = [errorGeneric, errorSize, errorType, errorMulti];

    errorElements.forEach((element) => element?.classList.remove('visible'));
  };

  const validateFileExtension = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileType = file.type.toLowerCase(); // Get the MIME type

    if (allowedExtensions && fileExtension && !allowedExtensions.includes(fileExtension)) {
      throw new Error('Invalid file type');
    }

    // Explicitly check for HEIC and HEIF MIME types (common for iOS)
    const forbiddenMimeTypes = ['image/heic', 'image/heif'];
    if (forbiddenMimeTypes.includes(fileType)) {
      throw new Error('Invalid file type');
    }
  };

  const validateFileSize = (file: File) => {
    //Prepare data for validations
    const fileSize = file.size / 1024; // in KB
    const maxSize = maxFileSize * 1000; // in KB
    if (fileSize > maxSize) {
      throw new Error('File is too large');
    }
  };

  const validateMultipleFiles = (files: FileList) => {
    if (files.length > 1) {
      throw new Error('Only one file is allowed');
    }
  };

  const validateFile = (files: FileList) => {
    const file = files[0];
    validateMultipleFiles(files);
    validateFileExtension(file);
    validateFileSize(file);
  };

  const handleCancelUpload = () => {
    inactiveUploading();
    if (input) {
      input.value = '';
    }
  };

  const handleErrors = (error: Error) => {
    inactiveUploading();

    if (input) {
      input.value = '';
    }

    const errorMap: { [key: string]: HTMLElement | null } = {
      'Invalid file type': errorType,
      'File is too large': errorSize,
      'Only one file is allowed': errorMulti,
    };

    const targetElement = errorMap[error.message];

    if (targetElement) {
      targetElement.classList.add('visible');
    } else {
      errorGeneric?.classList.add('visible');
      errorGeneric!.textContent = error.message;
    }
  };

  const handleFileUpload = (files: FileList, currentInput: HTMLInputElement) => {
    try {
      const file = files[0];

      // reset errors
      removeErrorVisibility();

      // set uploading status
      activeUploading(file);

      // validate file
      validateFile(files);

      // remove required error
      const errorMessage = currentInput?.parentElement?.querySelector('.vr-mandatory-field');
      errorMessage?.classList.add('input-hidden-error-messagge-vr');
    } catch (error) {
      // reset uploading status
      inactiveUploading();

      // reset file input
      if (input) {
        input.value = '';
        input.setCustomValidity('Invalid file');
      }

      // handle errors
      handleErrors(error as Error);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    inactiveDragover();
    droparea?.classList.add('is-uploading');
    const dt = e.dataTransfer;
    const files = dt?.files;
    const currentInput = (e.target as HTMLElement)
      ?.closest('.cmp-file-uploader__container')
      ?.querySelector('.cmp-file-uploader__input') as HTMLInputElement;

    if (files && files.length > 0) {
      const dataTransfer = new DataTransfer();
      for (let i = 0; i < files.length; i++) {
        dataTransfer.items.add(files[i]);
      }

      currentInput.files = dataTransfer.files;

      handleFileUpload(currentInput.files, currentInput);
    }
  };

  ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((evtName) => {
    droparea?.addEventListener(evtName, (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });

  ['dragenter', 'dragover'].forEach((evtName: string) => {
    droparea?.addEventListener(evtName, activeDragover);
  });

  ['dragleave', 'drop'].forEach((evtName: string) => {
    droparea?.addEventListener(evtName, inactiveDragover);
  });

  droparea?.addEventListener('drop', handleDrop);

  button?.addEventListener('click', (e) => {
    e.preventDefault(); //Prevent default because it scrolls to top of page when clicking the button
    if (!uploadingStatus) {
      input?.click();
    } else {
      handleCancelUpload();
    }
  });

  input?.addEventListener('change', (e: Event) => {
    const files = (e.target as HTMLInputElement).files;
    const currentInput = e.target as HTMLInputElement;
    if (files) {
      handleFileUpload(files, currentInput);
    }
  });
}

fileUploader();

export function validateFileUploader(fileUploaderInput: HTMLInputElement): boolean {
  // Check if the input is mandatory
  const isMandatory = fileUploaderInput.getAttribute('data-validation') === 'vr-mandatory-field';

  // If not mandatory, validation passes
  if (!isMandatory) {
    return true;
  }

  // Check if the file is uploading and has a value
  const parentElement = fileUploaderInput.parentElement;
  const isUploadingAndHasValue =
    parentElement?.classList.contains('is-uploading') && fileUploaderInput.value;

  // If the file is uploading and has a file, return true
  if (isUploadingAndHasValue) {
    return true;
  }

  // Show the error message if validation fails
  const errorMessage = parentElement?.querySelector('.vr-mandatory-field');
  errorMessage?.classList.remove('input-hidden-error-message-vr');

  return false;
}
